/* eslint-disable newline-before-return */
// helpers/enrichImpacts.ts
import {
  FieldImpact,
  FormField,
  FormOption,
  FormSection,
  FormStep,
  FormSubSection,
  ParsedForm,
} from "@/components/FormEngine/types";

// ========================================
// TYPES ET INTERFACES
// ========================================

/**
 * Référence vers un champ cible dans le formulaire
 * Utilisée pour mapper les noms d'impacts vers les champs réels
 */
interface TargetFieldReference {
  /** Identifiant unique du champ */
  id: string;
  /** Chemin complet vers le champ dans la hiérarchie du formulaire */
  path: string[];
}

/**
 * Index des champs du formulaire
 * Mappe les clés de résolution (ex: "section.field") vers les références de champs
 */
type FieldIndex = Map<string, TargetFieldReference>;

// ========================================
// CONSTRUCTION DE L'INDEX DES CHAMPS
// ========================================

/**
 * Construit un index de tous les champs du formulaire pour une résolution rapide
 *
 * L'index mappe les chemins simplifiés (sans le nom du formulaire racine)
 * vers les références complètes des champs
 *
 * @param parsedForm - Le formulaire parsé contenant tous les champs
 * @returns Un Map indexant tous les champs par leur chemin simplifié
 *
 * @example
 * // Pour un champ avec path: ["productCreationForm", "test_ifs", "b"]
 * // La clé dans l'index sera: "test_ifs.b"
 * // La valeur sera: { id: "field-123", path: ["productCreationForm", "test_ifs", "b"] }
 */
const buildParsedFieldIndex = (parsedForm: ParsedForm): FieldIndex => {
  const fieldIndex = new Map<string, TargetFieldReference>();

  // Parcourir toute la structure du formulaire
  const allFields = extractAllFieldsFromForm(parsedForm);

  // Indexer chaque champ
  for (const field of allFields) {
    const indexKey = createFieldIndexKey(field.path);
    const fieldReference = createFieldReference(field);
    fieldIndex.set(indexKey, fieldReference);
  }

  return fieldIndex;
};

/**
 * Extrait tous les champs de toutes les sections du formulaire
 *
 * @param parsedForm - Le formulaire à parcourir
 * @returns Liste de tous les champs du formulaire
 */
function extractAllFieldsFromForm(parsedForm: ParsedForm): FormField[] {
  const allFields: FormField[] = [];

  // Parcourir toutes les étapes
  for (const [_stepKey, step] of parsedForm.steps) {
    const stepFields = extractFieldsFromStep(step);
    allFields.push(...stepFields);
  }

  return allFields;
}

/**
 * Extrait tous les champs d'une étape
 *
 * @param step - L'étape à parcourir
 * @returns Les champs de l'étape
 */
function extractFieldsFromStep(step: FormStep): FormField[] {
  const stepFields: FormField[] = [];

  // Parcourir toutes les sections de l'étape
  for (const [_sectionKey, section] of step.sections) {
    const sectionFields = extractFieldsFromSection(section);
    stepFields.push(...sectionFields);
  }

  return stepFields;
}

/**
 * Extrait tous les champs d'une section
 *
 * @param section - La section à parcourir
 * @returns Les champs de la section
 */
function extractFieldsFromSection(section: FormSection): FormField[] {
  const sectionFields: FormField[] = [];

  // Parcourir toutes les sous-sections
  for (const [_subSectionKey, subSection] of section.subSections) {
    const subSectionFields = extractFieldsFromSubSection(subSection);
    sectionFields.push(...subSectionFields);
  }

  return sectionFields;
}

/**
 * Extrait tous les champs d'une sous-section
 *
 * @param subSection - La sous-section à parcourir
 * @returns Les champs de la sous-section
 */
function extractFieldsFromSubSection(subSection: FormSubSection): FormField[] {
  const subSectionFields: FormField[] = [];

  // Parcourir toutes les lignes de la sous-section
  for (const row of subSection.rows) {
    // Ajouter tous les champs de la ligne
    subSectionFields.push(...row.fields);
  }

  return subSectionFields;
}

/**
 * Crée la clé d'indexation pour un champ
 * Retire le premier élément du chemin (nom du formulaire racine)
 *
 * @param fieldPath - Le chemin complet du champ
 * @returns La clé d'indexation (chemin sans le premier élément)
 *
 * @example
 * createFieldIndexKey(["productCreationForm", "test_ifs", "b"])
 * // Retourne: "test_ifs.b"
 */
function createFieldIndexKey(fieldPath: string[]): string {
  // Retirer le premier élément (nom du formulaire racine)
  const pathWithoutRoot = fieldPath.slice(1);

  // Joindre les éléments restants avec des points
  return pathWithoutRoot.join(".");
}

/**
 * Crée une référence vers un champ
 *
 * @param field - Le champ pour lequel créer une référence
 * @returns La référence contenant l'ID et le chemin
 */
function createFieldReference(field: FormField): TargetFieldReference {
  return {
    id: field.id,
    path: field.path,
  };
}

// ========================================
// ENRICHISSEMENT DES IMPACTS
// ========================================

/**
 * Enrichit les impacts des options avec les IDs et chemins réels des champs cibles
 *
 * Les impacts dans le formulaire initial ne contiennent que les noms des champs.
 * Cette fonction les enrichit avec les IDs et chemins complets pour faciliter
 * leur résolution lors de l'exécution.
 *
 * Note: Cette fonction modifie le formulaire en place pour des raisons de performance
 *
 * @param parsedForm - Le formulaire à enrichir
 * @returns Le même formulaire avec les impacts enrichis (modifié en place)
 *
 * @example
 * // Avant enrichissement:
 * // impact: { name: "test_ifs.b", properties: { hidden: true } }
 *
 * // Après enrichissement:
 * // impact: {
 * //   name: "test_ifs.b",
 * //   id: "field-123",
 * //   path: ["productCreationForm", "test_ifs", "b"],
 * //   properties: { hidden: true }
 * // }
 */
export const enrichImpacts = (parsedForm: ParsedForm): ParsedForm => {
  // Étape 1: Construire l'index de tous les champs
  const fieldIndex = buildParsedFieldIndex(parsedForm);

  // Étape 2: Enrichir tous les impacts du formulaire
  enrichAllFormImpacts(parsedForm, fieldIndex);

  // Retourner le formulaire modifié
  return parsedForm;
};

/**
 * Enrichit tous les impacts dans le formulaire entier
 *
 * @param parsedForm - Le formulaire contenant les impacts
 * @param fieldIndex - L'index des champs pour la résolution
 */
function enrichAllFormImpacts(
  parsedForm: ParsedForm,
  fieldIndex: FieldIndex,
): void {
  // Parcourir tous les champs pour enrichir leurs options
  for (const [_stepKey, step] of parsedForm.steps) {
    enrichStepImpacts(step, fieldIndex);
  }
}

/**
 * Enrichit les impacts dans une étape
 *
 * @param step - L'étape à traiter
 * @param fieldIndex - L'index des champs
 */
function enrichStepImpacts(step: FormStep, fieldIndex: FieldIndex): void {
  for (const [_sectionKey, section] of step.sections) {
    enrichSectionImpacts(section, fieldIndex);
  }
}

/**
 * Enrichit les impacts dans une section
 *
 * @param section - La section à traiter
 * @param fieldIndex - L'index des champs
 */
function enrichSectionImpacts(
  section: FormSection,
  fieldIndex: FieldIndex,
): void {
  for (const [_subSectionKey, subSection] of section.subSections) {
    enrichSubSectionImpacts(subSection, fieldIndex);
  }
}

/**
 * Enrichit les impacts dans une sous-section
 *
 * @param subSection - La sous-section à traiter
 * @param fieldIndex - L'index des champs
 */
function enrichSubSectionImpacts(
  subSection: FormSubSection,
  fieldIndex: FieldIndex,
): void {
  for (const row of subSection.rows) {
    for (const field of row.fields) {
      enrichFieldOptions(field, fieldIndex);
    }
  }
}

/**
 * Enrichit les impacts de toutes les options d'un champ
 *
 * @param field - Le champ dont les options doivent être enrichies
 * @param fieldIndex - L'index des champs pour la résolution
 */
function enrichFieldOptions(field: FormField, fieldIndex: FieldIndex): void {
  // Si le champ n'a pas d'options, rien à faire
  if (!field.options) {
    return;
  }

  // Enrichir chaque option
  for (const option of field.options) {
    enrichOptionImpacts(option, fieldIndex);
  }
}

/**
 * Enrichit les impacts d'une option spécifique
 * Ajoute l'ID et le chemin du champ cible à chaque impact
 *
 * @param option - L'option dont les impacts doivent être enrichis
 * @param fieldIndex - L'index des champs pour la résolution
 */
function enrichOptionImpacts(option: FormOption, fieldIndex: FieldIndex): void {
  // Si l'option n'a pas d'impacts, rien à faire
  if (!option.impacts) {
    return;
  }

  // Enrichir chaque impact avec les informations du champ cible
  option.impacts = option.impacts.map((impact) =>
    enrichSingleImpact(impact, fieldIndex),
  );
}

/**
 * Enrichit un impact unique avec les informations du champ cible
 *
 * @param impact - L'impact à enrichir
 * @param fieldIndex - L'index des champs pour la résolution
 * @returns L'impact enrichi avec ID et chemin
 */
function enrichSingleImpact(
  impact: FieldImpact,
  fieldIndex: FieldIndex,
): FieldImpact {
  // Rechercher la référence du champ cible dans l'index
  const targetFieldReference = fieldIndex.get(impact.name);

  // Si le champ n'est pas trouvé, utiliser des valeurs par défaut
  if (!targetFieldReference) {
    return {
      ...impact,
      id: "",
      path: [],
    };
  }

  // Enrichir l'impact avec les informations trouvées
  return {
    ...impact,
    id: targetFieldReference.id,
    path: targetFieldReference.path,
  };
}
